import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { apiPost } from '../../lib/api.js';

const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
// Module scope so Stripe.js is initialised once.
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

const cardStyle = {
  style: {
    base: { color: '#ffffff', fontSize: '16px', '::placeholder': { color: 'rgba(255,255,255,0.4)' } },
    invalid: { color: '#f87171' },
  },
};

function CardForm({ onSaved }) {
  const stripe = useStripe();
  const elements = useElements();
  const [agree, setAgree] = useState(false);
  const [complete, setComplete] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save(e) {
    e.preventDefault();
    if (!stripe || !elements || !agree || !complete) return;
    setBusy(true);
    try {
      const { clientSecret, setupIntentId } = await apiPost('/billing-center/setup-intent', {});
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (error) throw new Error(error.message || 'Card could not be saved');
      await apiPost('/billing-center/setup-intent/confirm', { setupIntentId: setupIntent?.id || setupIntentId });
      toast.success('Payment method saved');
      elements.getElement(CardElement)?.clear();
      setAgree(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Could not save card');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="space-y-3">
      <div className="text-xs text-white/50">Add card</div>
      <div className="border-b border-white/20 pb-2"><CardElement options={cardStyle} onChange={(ev) => setComplete(ev.complete)} /></div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
        I agree to the <Link to="/terms" target="_blank" className="text-civix-purpleSoft font-semibold hover:underline">Terms of Service</Link>.
      </label>
      <div className="flex justify-end">
        <button type="submit" disabled={!agree || !complete || busy} className="btn-primary text-sm disabled:opacity-40 flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} SAVE PAYMENT METHOD
        </button>
      </div>
    </form>
  );
}

/**
 * Stripe card entry (card data goes straight to Stripe, never to our servers).
 * @param {{onSaved: () => void}} props
 */
export default function AddCardForm({ onSaved }) {
  if (!stripePromise) {
    return <p className="text-xs text-amber-300">Card entry is unavailable: <code>VITE_STRIPE_PUBLISHABLE_KEY</code> is not set for this build.</p>;
  }
  return <Elements stripe={stripePromise}><CardForm onSaved={onSaved} /></Elements>;
}
